import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useProjectTemplates } from '../hooks/useProjectTemplates';
import { useProjectTemplate } from '../hooks/useProjectTemplate';
import { useDeleteProjectTemplate } from '../hooks/useProjectTemplateMutations';
import { TemplateSectorFilter } from './TemplateSectorFilter';
import { TemplateCard } from './TemplateCard';
import { NewTemplateTile } from './NewTemplateTile';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';
import { TemplateEditorDialog } from './TemplateEditorDialog';
import { TemplateMetadataDialog } from './TemplateMetadataDialog';
import type { TemplateGalleryItem, TemplateSector } from '../types';

interface TemplateGalleryTabProps {
  selectedTemplateId: string | undefined;
  onSelect: (templateId: string) => void;
}

export function TemplateGalleryTab({ selectedTemplateId, onSelect }: TemplateGalleryTabProps) {
  const { t } = useTranslation('projectTemplates');
  const [sector, setSector] = useState<TemplateSector | 'all'>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [metadataTarget, setMetadataTarget] = useState<TemplateGalleryItem | undefined>();

  const { templates, isLoading, isError, refetch } = useProjectTemplates(
    sector === 'all' ? {} : { sector },
  );
  const { template: selectedTemplate } = useProjectTemplate(selectedTemplateId);
  const deleteTemplate = useDeleteProjectTemplate();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  async function handleDelete(t2: TemplateGalleryItem) {
    const result = await confirm({
      title: t('delete.confirmTitle'),
      description: t('delete.confirmDescription'),
      variant: 'destructive',
    });
    if (result.confirmed) {
      deleteTemplate.mutate(t2.id);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4" data-testid="project-templates-gallery-tab">
      <TemplateSectorFilter value={sector} onChange={setSector} />

      <div className="flex flex-1 gap-4 overflow-hidden md:flex-row flex-col">
        <div className="grid shrink-0 grid-cols-2 gap-3 overflow-y-auto md:w-[340px] md:grid-cols-2">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[104px] w-full rounded-lg" />
            ))
          ) : isError ? (
            <div
              className="col-span-2 py-6 text-center"
              data-testid="project-templates-gallery-error"
            >
              <p className="text-sm text-muted-foreground">{t('gallery.error')}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
                {t('gallery.retry')}
              </Button>
            </div>
          ) : templates.length === 0 ? (
            <div
              className="col-span-2 py-6 text-center"
              data-testid="project-templates-gallery-empty"
            >
              <p className="text-sm text-muted-foreground">{t('gallery.empty')}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setSector('all')}>
                {t('gallery.emptyAction')}
              </Button>
            </div>
          ) : (
            <>
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  selected={template.id === selectedTemplateId}
                  onSelect={() => onSelect(template.id)}
                  onEdit={() => setMetadataTarget(template)}
                  onDelete={() => handleDelete(template)}
                />
              ))}
              <NewTemplateTile onClick={() => setEditorOpen(true)} />
            </>
          )}
        </div>

        <div className="flex-1 overflow-hidden rounded-lg border border-border p-3">
          {selectedTemplate ? (
            <TemplatePreviewPanel template={selectedTemplate} />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('gallery.empty')}</p>
          )}
        </div>
      </div>

      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onCreated={(templateId) => onSelect(templateId)}
      />

      {metadataTarget && (
        <TemplateMetadataDialog
          template={metadataTarget}
          open={!!metadataTarget}
          onOpenChange={(open) => !open && setMetadataTarget(undefined)}
        />
      )}

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
